// S-B1: Bank Account Connection Component
// Handles Plaid Link integration for connecting bank accounts

import React, { useState } from 'react';
import { Button, Alert, Spin, message } from 'antd';
import { BankOutlined, LoadingOutlined } from '@ant-design/icons';
import { usePlaidLink } from 'react-plaid-link';
import { useTranslation } from 'react-i18next';
import { paymentsEndpoints } from '../api/paymentsEndpoints';

interface BankAccountConnectProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const BankAccountConnect: React.FC<BankAccountConnectProps> = ({
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation('payments');
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch link token from backend
  const fetchLinkToken = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await paymentsEndpoints.createLinkToken();
      setLinkToken(response.link_token);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t('bankAccounts.connect.error.initFailed');
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle successful Plaid Link flow
  const onPlaidSuccess = async (publicToken: string, metadata: any) => {
    setLoading(true);
    setError(null);

    try {
      await paymentsEndpoints.exchangePublicToken(publicToken);

      message.success(t('bankAccounts.connect.success'));
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t('bankAccounts.connect.error.connectFailed');
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle Plaid Link exit/cancel
  const onPlaidExit = (error: any, metadata: any) => {
    if (error) {
      console.error('Plaid Link error:', error);
      setError(error.error_message || 'Bank connection was interrupted');
    }
    
    // Reset link token so user can try again
    setLinkToken(null);
    onCancel?.();
  };

  // Initialize Plaid Link
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit
  });

  // Auto-fetch link token on mount
  React.useEffect(() => {
    fetchLinkToken();
  }, []);

  // Auto-open Plaid Link when token is ready
  React.useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  // Start flow by fetching link token
  const handleConnectClick = () => {
    fetchLinkToken();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        <p style={{ marginTop: '1rem', color: '#666' }}>
          {linkToken ? t('bankAccounts.connect.connecting') : t('bankAccounts.connect.initializing')}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert
          message={t('bankAccounts.connect.error.title')}
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '1rem' }}
        />
        <Button onClick={handleConnectClick} type="primary">
          {t('bankAccounts.connect.error.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <BankOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: '1rem' }} />
      <h3>{t('bankAccounts.connect.title')}</h3>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        {t('bankAccounts.connect.description')}
      </p>
      <Button
        type="primary"
        size="large"
        icon={<BankOutlined />}
        onClick={handleConnectClick}
        loading={loading}
      >
        {t('bankAccounts.connect.button')}
      </Button>
    </div>
  );
};

export default BankAccountConnect;
